'use client';

import * as React from 'react';
import { Table } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * DataTablePagination Props
 *
 * Props for the DataTablePagination component.
 */
export interface DataTablePaginationProps<TData> {
  /** The table instance from TanStack Table */
  table: Table<TData>;
}

/**
 * DataTablePagination Component
 *
 * A comprehensive pagination component for data tables with page size selection,
 * navigation controls, and status information.
 *
 * Features:
 * - Page size selector (10, 25, 50, 100 rows per page)
 * - First/Previous/Next/Last page navigation buttons
 * - Current page indicator (e.g., "Page 1 of 10")
 * - Row count display (e.g., "Showing 1-25 of 250 rows")
 * - Selected rows count (e.g., "3 rows selected")
 * - Smart button disabling (first page, last page)
 * - Mobile-responsive layout
 * - Dark mode support
 *
 * @example
 * ```tsx
 * <DataTablePagination table={table} />
 * ```
 */
export function DataTablePagination<TData>({
  table,
}: DataTablePaginationProps<TData>) {
  // Get pagination state
  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;
  const pageCount = table.getPageCount();
  const totalRows = table.getFilteredRowModel().rows.length;
  const selectedRowCount = table.getFilteredSelectedRowModel().rows.length;

  // Calculate current page range
  const startRow = pageIndex * pageSize + 1;
  const endRow = Math.min((pageIndex + 1) * pageSize, totalRows);

  return (
    <div className="flex flex-col gap-4 px-2 py-4 sm:flex-row sm:items-center sm:justify-between">
      {/* Left section: Row selection info */}
      <div className="flex-1 text-sm text-text-secondary">
        {selectedRowCount > 0 ? (
          <span>
            {selectedRowCount} of {totalRows} row(s) selected
          </span>
        ) : (
          <span>
            Showing {totalRows > 0 ? startRow : 0}-{endRow} of {totalRows} row(s)
          </span>
        )}
      </div>

      {/* Right section: Page controls */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6 lg:gap-8">
        {/* Rows per page selector */}
        <div className="flex items-center space-x-2">
          <p className="whitespace-nowrap text-sm font-medium text-text-primary">
            Rows per page
          </p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page indicator */}
        <div className="flex items-center justify-center text-sm font-medium text-text-primary">
          Page {pageIndex + 1} of {pageCount > 0 ? pageCount : 1}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center space-x-2">
          {/* First page button */}
          <Button
            variant="outline"
            size="sm"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            aria-label="Go to first page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          {/* Previous page button */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Go to previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Next page button */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Go to next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page button */}
          <Button
            variant="outline"
            size="sm"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!table.getCanNextPage()}
            aria-label="Go to last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
